import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "../lib/api";
import { groupedResponseToMap } from "../lib/lookups";
import { permissionForPath as pathToPermission } from "../lib/permissions";
import { ensurePushSubscriptionQuiet, unsubscribeFromPush } from "../lib/pushNotifications";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [lookupOptions, setLookupOptions] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("bansal_token");
    if (!token) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const restoreSession = async () => {
      let attempt = 0;
      while (!cancelled) {
        try {
          const r = await api.get("/auth/session", { skipDedupe: true });
          if (cancelled) return;
          setUser(r.data.user);
          setLookupOptions(r.data.lookup_options || null);
          ensurePushSubscriptionQuiet();
          setLoading(false);
          return;
        } catch (err) {
          const status = err?.response?.status;
          // Invalid/expired token only — never wipe session on deploy/network blips.
          if (status === 401) {
            if (cancelled) return;
            localStorage.removeItem("bansal_token");
            setUser(null);
            setLookupOptions(null);
            setLoading(false);
            return;
          }
          attempt += 1;
          const waitMs = Math.min(8000, 600 * 2 ** Math.min(attempt - 1, 4));
          await new Promise((r) => setTimeout(r, waitMs));
        }
      }
    };

    restoreSession();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("bansal_token", data.access_token);
    setUser(data.user);
    try {
      const session = await api.get("/auth/session");
      setLookupOptions(session.data.lookup_options || null);
    } catch {
      setLookupOptions(null);
    }
    ensurePushSubscriptionQuiet();
    return data.user;
  };

  const logout = () => {
    const finish = () => {
      localStorage.removeItem("bansal_token");
      setUser(null);
      setLookupOptions(null);
      window.location.href = "/login";
    };
    Promise.race([
      unsubscribeFromPush(),
      new Promise((resolve) => setTimeout(resolve, 1500)),
    ]).finally(finish);
  };

  const setLookupMapFromResponse = (data) => {
    setLookupOptions(data || null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        lookupOptions,
        setLookupMapFromResponse,
        loading,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function permissionForPath(pathname) {
  return pathToPermission(pathname);
}

/** @deprecated Prefer hasPermission(user, code) */
export function hasRole(user, allowedRoles) {
  if (!user) return false;
  const allowed = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];
  const held = userRoles(user);
  if (held.includes("admin")) return true;
  return allowed.some((r) => held.includes(r));
}

/** All role slugs assigned to the user (primary + extras). */
export function userRoles(user) {
  if (!user) return [];
  if (Array.isArray(user.roles) && user.roles.length) {
    return user.roles;
  }
  const extras = Array.isArray(user.extra_roles) ? user.extra_roles : [];
  return [user.role, ...extras].filter(Boolean);
}

export function isAdmin(user) {
  return userRoles(user).includes("admin");
}

export function hasPermission(user, code) {
  if (!user || !code) return false;
  const perms = user.permissions || [];
  if (perms.includes("rbac.manage") || isAdmin(user)) return true;
  return perms.includes(code);
}

/** Material and freight voucher editing — accounts workspace + explicit permission. */
export function canEditVouchers(user) {
  if (!user) return false;
  if (hasRole(user, ["admin", "accounts", "accounts_manager"])) return true;
  return hasPermission(user, "vouchers.edit");
}

/** Tally company-security login for Approve & Sync (own credentials). */
export function canEditTallyCredentials(user) {
  return hasRole(user, ["admin", "accounts", "accounts_manager"]);
}

export function hasAnyPermission(user, codes) {
  if (!user || !codes?.length) return false;
  return codes.some((c) => hasPermission(user, c));
}

export function isProductionForeman(user) {
  return hasPermission(user, "production.foreman.team.view");
}
